import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { db } from '../firebaseConfig';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Button,
    IconButton,
    Chip,
    Collapse
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddVendorDialog from '../components/AddVendorDialog';
import EditVendorDialog from '../components/EditVendorDialog';
import DeleteIcon from '@mui/icons-material/Delete';

interface Vendor {
    id: string;
    vendorId: string;
    companyName: string;
    contactFirstName: string;
    contactLastName: string;
    loginEmail?: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    taxId?: string;
    revenueSharePercent?: number;
    contractStartDate?: string;
    nmiSecurityKeyLast4?: string;
    nmiTokenizationKey?: string;
    nmiAccountStatus?: string;
    preferredPaymentGateway?: string;
    payoutSchedule?: string;
    payoutCurrency?: string;
    status: string;
}

function VendorRow({ vendor, onEdit, onPasswordReset, onDelete}: {
    vendor: Vendor;
    onEdit: (vendor: Vendor) => void;
    onPasswordReset: (loginEmail: string, companyName: string) => void;
    onDelete: (vendorId: string, companyName: string) => void;
}) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{vendor.vendorId}</TableCell>
                <TableCell>{vendor.companyName}</TableCell>
                <TableCell>{vendor.email}</TableCell>
                <TableCell>{vendor.phone}</TableCell>
                <TableCell>
                    <Chip
                        label={vendor.status}
                        color={vendor.status === 'active' ? 'success' : 'default'}
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onEdit(vendor)}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(vendor.id, vendor.companyName)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: { xs: 1, sm: 2 }, p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                gap: 1,
                                mb: 2
                            }}>
                                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    Partner Details
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    onClick={() => onPasswordReset(vendor.loginEmail || '', vendor.companyName)}
                                    disabled={!vendor.loginEmail}
                                >
                                    Reset Password
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Company & Contact */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Company Name
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.companyName}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Contact Person
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.contactFirstName} {vendor.contactLastName}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Emails */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Login Email (Firebase Auth)
                                        </Typography>
                                        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                                            {vendor.loginEmail || 'Not set'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Contact Email
                                        </Typography>
                                        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                                            {vendor.email}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Address */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Address
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.address || 'Not set'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            City, State, ZIP
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.city && vendor.state && vendor.zip
                                                ? `${vendor.city}, ${vendor.state} ${vendor.zip}`
                                                : 'Not set'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Phone & Status */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Phone
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.phone}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Status
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.status}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Business Info */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Tax ID / EIN
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.taxId || 'Not set'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Revenue Share
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.revenueSharePercent ? `${vendor.revenueSharePercent}%` : 'Not set'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Contract & Payment Gateway */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Contract Start Date
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.contractStartDate || 'Not set'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Preferred Gateway
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.preferredPaymentGateway?.toUpperCase() || 'Not set'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Payout Info */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Payout Schedule
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.payoutSchedule ? vendor.payoutSchedule.charAt(0).toUpperCase() + vendor.payoutSchedule.slice(1) : 'Not set'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Payout Currency
                                        </Typography>
                                        <Typography variant="body1">
                                            {vendor.payoutCurrency || 'Not set'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* NMI Payment Gateway */}
                                {(vendor.nmiSecurityKeyLast4 || vendor.nmiTokenizationKey) && (
                                    <Box>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            NMI Account
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#e0e0e0', p: 1, borderRadius: 1 }}>
                                            Security Key: {vendor.nmiSecurityKeyLast4 ? `••••••${vendor.nmiSecurityKeyLast4}` : 'Not set'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#e0e0e0', p: 1, borderRadius: 1, mt: 0.5 }}>
                                            Tokenization Key: {vendor.nmiTokenizationKey || 'Not set'}
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                            Status: {vendor.nmiAccountStatus || 'Unknown'}
                                            {(!vendor.nmiSecurityKeyLast4 || !vendor.nmiTokenizationKey) && ' — incomplete: both keys required'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function Vendors() {
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'vendors'));
            const vendorsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Vendor[];
            setVendors(vendorsData);
        } catch (error) {
            console.error('Error fetching vendors:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async (loginEmail: string, companyName: string) => {
        if (!loginEmail) {
            alert('No login email set for this partner');
            return;
        }

        if (!window.confirm(`Send password reset email to ${loginEmail} for ${companyName}?`)) {
            return;
        }

        try {
            const auth = getAuth();
            await sendPasswordResetEmail(auth, loginEmail);
            alert(`Password reset email sent to ${loginEmail}`);
        } catch (error: any) {
            console.error('Error sending password reset:', error);
            alert(`Failed to send password reset email: ${error.message}`);
        }
    };

    const handleDeleteVendor = async (vendorId: string, companyName: string) => {
        if (!window.confirm(`Are you sure you want to delete partner "${companyName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteDoc(doc(db, 'vendors', vendorId));
            alert('Partner deleted successfully');
            fetchVendors();
        } catch (error: any) {
            console.error('Error deleting vendor:', error);
            alert(`Failed to delete partner: ${error.message}`);
        }
    };

    useEffect(() => {
        fetchVendors();
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 1,
                mb: 2
            }}>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    Partners
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                    size="small"
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Add New Partner
                </Button>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 700 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Partner ID</TableCell>
                            <TableCell>Company Name</TableCell>
                            <TableCell>Contact Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vendors.map((vendor) => (
                            <VendorRow
                                key={vendor.id}
                                vendor={vendor}
                                onEdit={(v) => {
                                    setSelectedVendor(v);
                                    setEditDialogOpen(true);
                                }}
                                onPasswordReset={handlePasswordReset}
                                onDelete={handleDeleteVendor}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            </Box>

            <AddVendorDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSuccess={fetchVendors}
            />

            <EditVendorDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setSelectedVendor(null);
                }}
                vendor={selectedVendor}
                onSuccess={fetchVendors}
            />
        </Box>
    );
}